<template>
  <page
    v-model:filter="q"
    :name="$t('I miei clienti')"
    :description="$t('Accedi al pannello dei clienti o richiedi l\'accesso')"
    :state="[myCustomersResult, loginaziendeResult, affiliationLinkResult]"
  >
    <pf-page-section no-body-wrapper>
      <pf-title h="2">{{ $t('Clienti assegnati') }}</pf-title>

      <customers-list :customers="directCustomers" :filter="q" ouia-id="assigned-customer-table" />

      <pf-title h="2">{{ $t('Aziende presentate') }}</pf-title>

      <pf-list v-if="presentedCustomers.length" ouia-id="presented-customer-list">
        <pf-list-item v-for="c in presentedCustomers" :key="c.id">{{ c.ragione_sociale }}</pf-list-item>
      </pf-list>
      <p v-else>{{ $t('Nessuna azienda presentata') }}</p>

      <template v-if="affiliationLink">
        <pf-flex align-items="stretch" gap="md">
          <pf-card style="flex: 1; min-width: 300px">
            <pf-card-header>
              <pf-card-title>
                <pf-title h="2">{{ $t('Link di affiliazione') }}</pf-title>
                <p>{{ $t("Utilizzando questo url in fase di registrazione l'utente verrà aggiunto automaticamente alla vostra lista clienti.") }}</p>
              </pf-card-title>
            </pf-card-header>
            <pf-card-body>
              <pf-input-group>
                <x-input :model-value="affiliationLink" :aria-label="$t('Link di affiliazione')" readonly>
                  <template #append>
                    <pf-button @click="copyToClipBoard(affiliationLink)" variant="control">{{ $t('Copia Link') }}</pf-button>
                  </template>
                </x-input>
              </pf-input-group>
              <p>
                <i18n-t keypath="Oppure puoi creare direttamente un un'azienda prospect {0}">
                  <a href="#crea_prospect">{{ $t('compilando il form qui sotto') }}</a>
                </i18n-t>
              </p>
            </pf-card-body>
          </pf-card>

          <pf-card style="flex: 1; min-width: 300px">
            <pf-card-header>
              <pf-card-title>
                <pf-title h="2">{{ $t('Artera partner badge') }}</pf-title>
                <p>{{ $t('Incolla uno di questi codici sul tuo sito per mostrare il badge "Partner Artera" con il tuo link di affiliazione') }}</p>
              </pf-card-title>
            </pf-card-header>
            <pf-card-body>
              <pf-grid gutter>
                <pf-grid-item md="8">
                  <pf-title h="4"
                    >{{ $t('Versione orizzontale') }}
                    <pf-button small variant="control" @click="copyToClipBoard(horizontalBadge)">{{ $t('Copia codice') }}</pf-button></pf-title
                  >
                  <pre class="pf-v6-c-code-block">{{ horizontalBadge }}</pre>
                </pf-grid-item>
                <pf-grid-item md="4" class="partner-badge-preview">
                  <a :href="affiliationLink">
                    <img src="https://resources.artera.net/brand/artera-partnerbadge-horizontal.svg" width="100" />
                  </a>
                </pf-grid-item>

                <pf-grid-item md="8">
                  <pf-title h="4"
                    >{{ $t('Versione verticale') }}
                    <pf-button small variant="control" @click="copyToClipBoard(verticalBadge)">{{ $t('Copia codice') }}</pf-button></pf-title
                  >
                  <pre class="pf-v6-c-code-block">{{ verticalBadge }}</pre>
                </pf-grid-item>
                <pf-grid-item md="4" class="partner-badge-preview">
                  <a :href="affiliationLink">
                    <img src="https://resources.artera.net/brand/artera-partnerbadge-vertical.svg" width="100" />
                  </a>
                </pf-grid-item>
              </pf-grid>
            </pf-card-body>
          </pf-card>
        </pf-flex>
      </template>

      <pf-card id="crea_prospect">
        <pf-card-header>
          <pf-card-title>
            <pf-title h="2">{{ $t("Crea un'azienda prospect") }}</pf-title>
            <p>{{ $t('Tramite questo form potrai inserire un nuovo prospect nel nostro sistema e generare offerte a lui intestate') }}</p>
          </pf-card-title>
        </pf-card-header>
        <pf-card-body>
          <form-prospect :key="formKey" @submit="newProspect" data-ouia-component-id="prospect-form" style="max-width: 80ch" />
        </pf-card-body>
      </pf-card>
    </pf-page-section>
  </page>
</template>

<style lang="scss" scoped>
.pf-v6-c-code-block {
  padding: var(--pf-v6-c-code-block__header--PaddingBlockStart) var(--pf-v6-c-code-block__header--PaddingInlineEnd)
    var(--pf-v6-c-code-block__header--PaddingBlockEnd) var(--pf-v6-c-code-block__header--PaddingInlineStart);
  overflow: scroll;
}
.partner-badge-preview {
  margin-top: 40px;
}
</style>

<script lang="ts" setup>
import { http } from '../http';
import { asyncComputedList, asyncComputed } from '@common/asyncComputed';
import FormProspect from '../components/form-prospect.vue';
import CustomersList from '../components/customers-list.vue';
import { CompanyAccessResource, MyCustomersWithPresentedResource, type CompanyAccess, type MyCustomersAccess, type MyPresentedCustomer } from '@/resources';
import { ref, computed } from 'vue';
import { copyToClipBoard } from '@common/utils';

const { state: myCustomersResult, result: myCustomers } = asyncComputedList(() => new MyCustomersWithPresentedResource().get());
const { state: loginaziendeResult, result: loginaziende } = asyncComputed(() => new CompanyAccessResource().get());
const { state: affiliationLinkResult, result: affiliationLink } = asyncComputed(
  async () => (await http.get('/rest/my_customers/affiliationlink')).data as string,
);

const q = ref(''); // search query
const formKey = ref(0); // utilizzata per effettuare il reset del form

const directCustomers = computed(() =>
  myCustomers.value.filter((c): c is MyCustomersAccess => !c.presented).map(c => ({ ...c, login: loginaziende.value[c.id] as CompanyAccess })),
);
const presentedCustomers = computed(() => myCustomers.value.filter((c): c is MyPresentedCustomer => c.presented));

const horizontalBadge = computed(
  () =>
    `<a href="${affiliationLink.value}" target="_blank">\n  <img src="https://resources.artera.net/brand/artera-partnerbadge-horizontal.svg" width="300">\n</a>`,
);
const verticalBadge = computed(
  () =>
    `<a href="${affiliationLink.value}" target="_blank">\n  <img src="https://resources.artera.net/brand/artera-partnerbadge-vertical.svg" width="100">\n</a>`,
);

function newProspect() {
  myCustomersResult.update();
  formKey.value += 1;
}
</script>
